import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import React from 'react';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import transitions from 'config/theme/transitions';

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    padding: 6px 0;
`;

const InputAndLabel = styled('div')`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledInput = styled('input')`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;

    &:checked + label::before,
    &:hover + label::before {
        background-color: ${colors.black};
        border: 1px solid ${colors.black};
        box-shadow: inset 0 0 0 2px ${colors.white};
    }

    &:focus-visible + label::before {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const Label = styled('label', { shouldForwardProp: prop => ['disabled', 'error', 'required'].indexOf(prop) === -1 })`
    font-size: 16px;
    line-height: 1.25;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    position: relative;

    &::before {
        content: '';
        display: inline-block;
        border: 1px solid ${({ error }) => (error ? colors.red : colors.black)};
        height: 16px;
        width: 16px;
        margin-right: 16px;
        flex-shrink: 0;
        transition: background ${transitions.primary};
    }

    ${({ required }) =>
        required &&
        `
        ::after {
            content: '*';
            display: block;
            position: absolute;
            top: 0;
            right: -10px;
        }
    `}

    ${({ disabled }) =>
        disabled &&
        `
        cursor: default;
        pointer-events: none;
        opacity: 0.5;
    `}
`;

const ErrorMessage = styled(Paragraph)`
    color: ${colors.error};
    font-size: 16px;
    margin: 4px 0 0 0;
`;

const Checkbox = ({ disabled = false, disableNativeRequired = false, error, handleChange = () => {}, handleBlur = () => {}, label, name, required = false }) => (
    <Wrapper>
        <InputAndLabel>
            <StyledInput
                aria-describedby={error ? `${name}-error` : undefined}
                aria-invalid={!!error}
                aria-required={required}
                disabled={disabled}
                id={name}
                name={name}
                required={required && !disableNativeRequired}
                type="checkbox"
                onBlur={handleBlur}
                onChange={handleChange}
            />
            <Label disabled={disabled} htmlFor={name} error={error ? 1 : 0} required={required}>
                {label}
            </Label>
        </InputAndLabel>
        {error && <ErrorMessage id={`${name}-error`}>*{error.message}</ErrorMessage>}
    </Wrapper>
);

Checkbox.propTypes = {
    disabled: PropTypes.bool,
    disableNativeRequired: PropTypes.bool,
    error: PropTypes.exact({
        message: PropTypes.string,
        name: PropTypes.string,
    }),
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
};

export default Checkbox;
